import { useCallback, useReducer } from "react";
import type { AddressFormValues } from "../types";
import type { AddressSuggestionDto } from "../../../generated/models";

export interface AddressLookupState {
  appliedSuggestion: AddressSuggestionDto | null;
  suggestionSource: "autocomplete" | "street-lookup" | null;
  lookupError: string | null;
  coordinates: { latitude: number; longitude: number } | null;
  coordinateLookupError: string | null;
  overrideDialogOpen: boolean;
  isModifiedFromSuggestion: boolean;
}

type AddressLookupAction =
  | { type: "SUGGESTION_APPLIED"; suggestion: AddressSuggestionDto; source: "autocomplete" | "street-lookup" }
  | { type: "FORM_CHANGED"; currentValues: AddressFormValues }
  | { type: "COORDINATES_RESOLVED"; coordinates: { latitude: number; longitude: number } }
  | { type: "COORDINATES_FAILED"; error: string }
  | { type: "LOOKUP_FAILED"; error: string }
  | { type: "LOOKUP_ERROR_CLEARED" }
  | { type: "OVERRIDE_DIALOG_OPENED" }
  | { type: "OVERRIDE_DIALOG_CLOSED" }
  | { type: "RESET" };

const initialState: AddressLookupState = {
  appliedSuggestion: null,
  suggestionSource: null,
  lookupError: null,
  coordinates: null,
  coordinateLookupError: null,
  overrideDialogOpen: false,
  isModifiedFromSuggestion: false,
};

function normalize(val: string | undefined | null): string {
  return (val ?? "").trim().toLowerCase();
}

function hasDiverged(suggestion: AddressSuggestionDto, current: AddressFormValues): boolean {
  return (
    normalize(suggestion.streetAddress) !== normalize(current.streetAddress) ||
    normalize(suggestion.streetAddress2) !== normalize(current.streetAddress2) ||
    normalize(suggestion.city) !== normalize(current.city) ||
    normalize(suggestion.state) !== normalize(current.state) ||
    normalize(suggestion.zip) !== normalize(current.zip) ||
    normalize(suggestion.country) !== normalize(current.country)
  );
}

function reducer(state: AddressLookupState, action: AddressLookupAction): AddressLookupState {
  switch (action.type) {
    case "SUGGESTION_APPLIED":
      return {
        ...state,
        appliedSuggestion: action.suggestion,
        suggestionSource: action.source,
        lookupError: null,
        coordinates: null,
        coordinateLookupError: null,
        isModifiedFromSuggestion: false,
      };
    case "FORM_CHANGED":
      if (!state.appliedSuggestion) return state;
      return {
        ...state,
        isModifiedFromSuggestion: hasDiverged(state.appliedSuggestion, action.currentValues),
      };
    case "COORDINATES_RESOLVED":
      return { ...state, coordinates: action.coordinates, coordinateLookupError: null };
    case "COORDINATES_FAILED":
      return { ...state, coordinateLookupError: action.error };
    case "LOOKUP_FAILED":
      return { ...state, lookupError: action.error };
    case "LOOKUP_ERROR_CLEARED":
      return { ...state, lookupError: null };
    case "OVERRIDE_DIALOG_OPENED":
      return { ...state, overrideDialogOpen: true };
    case "OVERRIDE_DIALOG_CLOSED":
      return { ...state, overrideDialogOpen: false };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

function suggestionToFormValues(suggestion: AddressSuggestionDto): AddressFormValues {
  return {
    streetAddress: suggestion.streetAddress ?? "",
    streetAddress2: suggestion.streetAddress2 ?? "",
    city: suggestion.city ?? "",
    state: suggestion.state ?? "",
    zip: suggestion.zip ?? "",
    country: suggestion.country ?? "",
    siteName: "",
  };
}

export interface UseAddressLookupStateReturn {
  state: AddressLookupState;
  applySuggestion: (suggestion: AddressSuggestionDto, source: "autocomplete" | "street-lookup") => AddressFormValues;
  notifyFormChanged: (currentValues: AddressFormValues) => void;
  resolveCoordinates: (coords: { latitude: number; longitude: number }) => void;
  failCoordinates: (error: string) => void;
  failLookup: (error: string) => void;
  clearLookupError: () => void;
  openOverrideDialog: () => void;
  closeOverrideDialog: () => void;
  reset: () => void;
  shouldPromptOverride: boolean;
}

export default function useAddressLookupState(): UseAddressLookupStateReturn {
  const [state, dispatch] = useReducer(reducer, initialState);

  const applySuggestion = useCallback(
    (suggestion: AddressSuggestionDto, source: "autocomplete" | "street-lookup"): AddressFormValues => {
      dispatch({ type: "SUGGESTION_APPLIED", suggestion, source });
      return suggestionToFormValues(suggestion);
    },
    [],
  );

  const notifyFormChanged = useCallback(
    (currentValues: AddressFormValues) => {
      dispatch({ type: "FORM_CHANGED", currentValues });
    },
    [],
  );

  const resolveCoordinates = useCallback(
    (coords: { latitude: number; longitude: number }) => {
      dispatch({ type: "COORDINATES_RESOLVED", coordinates: coords });
    },
    [],
  );

  const failCoordinates = useCallback((error: string) => {
    dispatch({ type: "COORDINATES_FAILED", error });
  }, []);

  const failLookup = useCallback((error: string) => {
    dispatch({ type: "LOOKUP_FAILED", error });
  }, []);

  const clearLookupError = useCallback(() => {
    dispatch({ type: "LOOKUP_ERROR_CLEARED" });
  }, []);

  const openOverrideDialog = useCallback(() => {
    dispatch({ type: "OVERRIDE_DIALOG_OPENED" });
  }, []);

  const closeOverrideDialog = useCallback(() => {
    dispatch({ type: "OVERRIDE_DIALOG_CLOSED" });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  const shouldPromptOverride = state.appliedSuggestion !== null && state.isModifiedFromSuggestion;

  return {
    state,
    applySuggestion,
    notifyFormChanged,
    resolveCoordinates,
    failCoordinates,
    failLookup,
    clearLookupError,
    openOverrideDialog,
    closeOverrideDialog,
    reset,
    shouldPromptOverride,
  };
}
